import { useCallback, useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Globe } from "lucide-react";
import { cn } from "@/lib/utils";

type LangCode = "en" | "es" | "pt";

const LANGUAGES: { code: LangCode; name: string; flag: string }[] = [
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "es", name: "Español", flag: "🇪🇸" },
  { code: "pt", name: "Português", flag: "🇧🇷" },
];

const STORAGE_KEY = "preferred-language";
const SCRIPT_ID = "google-translate-script";
const HOST_ID = "google_translate_element";

const readCookieLang = (): LangCode | null => {
  const match = document.cookie.match(/(?:^|;\s*)googtrans=([^;]+)/);
  if (!match) return null;
  const parts = decodeURIComponent(match[1]).split("/");
  const code = parts[parts.length - 1] as LangCode;
  return LANGUAGES.some((l) => l.code === code) ? code : null;
};

const setCookieLang = (code: LangCode) => {
  const value = `/en/${code}`;
  const domain = window.location.hostname;
  const bare = domain.replace(/^www\./, "");
  document.cookie = `googtrans=${value};path=/`;
  document.cookie = `googtrans=${value};path=/;domain=.${bare}`;
};

const clearCookieLang = () => {
  const domain = window.location.hostname.replace(/^www\./, "");
  document.cookie = "googtrans=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie = `googtrans=;path=/;domain=.${domain};expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

/** Ensures the (visually hidden) Google Translate element exists exactly once. */
const ensureWidget = () =>
  new Promise<HTMLSelectElement | null>((resolve) => {
    if (!document.getElementById(HOST_ID)) {
      const host = document.createElement("div");
      host.id = HOST_ID;
      host.setAttribute("aria-hidden", "true");
      host.style.position = "absolute";
      host.style.width = "1px";
      host.style.height = "1px";
      host.style.overflow = "hidden";
      host.style.clip = "rect(0 0 0 0)";
      host.style.whiteSpace = "nowrap";
      document.body.appendChild(host);
    }

    if (!document.getElementById(SCRIPT_ID)) {
      (window as any).googleTranslateElementInit = () => {
        const google = (window as any).google;
        if (google?.translate?.TranslateElement) {
          new google.translate.TranslateElement(
            {
              pageLanguage: "en",
              includedLanguages: LANGUAGES.map((l) => l.code).join(","),
              autoDisplay: false,
            },
            HOST_ID
          );
        }
      };
      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src =
        "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      script.async = true;
      document.body.appendChild(script);
    }

    let tries = 0;
    const poll = window.setInterval(() => {
      const select = document.querySelector<HTMLSelectElement>("select.goog-te-combo");
      tries += 1;
      if (select || tries > 50) {
        window.clearInterval(poll);
        resolve(select ?? null);
      }
    }, 200);
  });

const LanguageSwitcher = ({ className }: { className?: string }) => {
  const [current, setCurrent] = useState<LangCode>("en");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as LangCode | null;
    const active = readCookieLang() ?? (stored && LANGUAGES.some((l) => l.code === stored) ? stored : null);
    if (active && active !== "en") {
      setCurrent(active);
      ensureWidget().then((select) => {
        if (select && select.value !== active) {
          select.value = active;
          select.dispatchEvent(new Event("change"));
        }
      });
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  const changeLanguage = useCallback(async (code: LangCode) => {
    setIsOpen(false);
    setCurrent(code);
    localStorage.setItem(STORAGE_KEY, code);

    if (code === "en") {
      clearCookieLang();
      const select = document.querySelector<HTMLSelectElement>("select.goog-te-combo");
      if (select) {
        select.value = "en";
        select.dispatchEvent(new Event("change"));
      }
      window.location.reload();
      return;
    }

    setCookieLang(code);
    const select = await ensureWidget();
    if (select) {
      select.value = code;
      select.dispatchEvent(new Event("change"));
    } else {
      window.location.reload();
    }
  }, []);

  const active = LANGUAGES.find((l) => l.code === current) ?? LANGUAGES[0];

  return (
    <div ref={containerRef} className={cn("relative notranslate", className)}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-label={`Change language, current language: ${active.name}`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className="flex h-11 min-w-[44px] items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
      >
        <span className="text-base leading-none" aria-hidden="true">
          {active.flag}
        </span>
        <span className="hidden sm:inline">{active.code.toUpperCase()}</span>
        <Globe className="h-4 w-4 sm:hidden" aria-hidden="true" />
        <ChevronDown
          className={cn("h-3.5 w-3.5 transition-transform", isOpen && "rotate-180")}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <ul
          role="listbox"
          aria-label="Select language"
          className="absolute right-0 z-50 mt-2 w-48 overflow-hidden rounded-md border border-border bg-popover p-1 shadow-lg"
        >
          {LANGUAGES.map((language) => (
            <li key={language.code}>
              <button
                type="button"
                role="option"
                aria-selected={language.code === current}
                onClick={() => changeLanguage(language.code)}
                className={cn(
                  "flex h-11 w-full items-center gap-3 rounded-sm px-3 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground",
                  language.code === current
                    ? "bg-accent/60 font-semibold text-accent-foreground"
                    : "text-popover-foreground"
                )}
              >
                <span className="text-base leading-none" aria-hidden="true">
                  {language.flag}
                </span>
                <span className="flex-1">{language.name}</span>
                {language.code === current && <Check className="h-4 w-4" aria-hidden="true" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
